<template>
    <v-container fluid>
        <v-row class="align-center mb-2">
            <v-col cols="auto">
                <h2 class="text-h6">Utenti</h2>
                <div class="text-body-2 text-medium-emphasis">Gestisci gli operatori e i visualizzatori della tua
                    azienda</div>
            </v-col>
            <v-spacer />
            <v-col cols="auto">
                <v-btn color="primary" prepend-icon="mdi-plus" @click="apriDialogUtente(null)">Nuovo utente</v-btn>
            </v-col>
        </v-row>

        <v-data-table :headers="headers" :items="utenti" :items-per-page="15" density="comfortable">
            <template #[`item.isDeleted`]="{ item }">
                <v-chip size="small" :color="item.isDeleted ? 'grey' : 'success'" variant="flat">
                    {{ item.isDeleted ? 'Disabilitato' : 'Attivo' }}
                </v-chip>
            </template>
            <template #[`item.azioni`]="{ item }">
                <v-btn icon variant="text" size="small" @click="apriDialogUtente(item)" title="Modifica">
                    <v-icon size="small">mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon variant="text" size="small" @click="apriDialogPassword(item)" title="Reset password">
                    <v-icon size="small">mdi-lock-reset</v-icon>
                </v-btn>
                <v-btn icon variant="text" size="small" @click="apriDialogEmbed(item)" title="Link di login automatico">
                    <v-icon size="small">mdi-link-variant</v-icon>
                </v-btn>
                <v-btn v-if="!item.isDeleted" icon variant="text" size="small" color="error" @click="disabilita(item)"
                    title="Disabilita">
                    <v-icon size="small">mdi-account-off</v-icon>
                </v-btn>
                <v-btn v-else icon variant="text" size="small" color="success" @click="abilita(item)" title="Riabilita">
                    <v-icon size="small">mdi-account-check</v-icon>
                </v-btn>
            </template>
        </v-data-table>

        <!-- Dialog utente -->
        <v-dialog v-model="dialogUtente" max-width="800" :fullscreen="isMobile">
            <v-card>
                <v-card-title>{{ formUtente.userId ? 'Modifica utente' : 'Nuovo utente' }}</v-card-title>
                <v-card-text>
                    <v-form ref="formUtenteRef">
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="formUtente.username" label="Username"
                                    :disabled="!!formUtente.userId" :rules="[v => !!v || 'Obbligatorio']" />
                            </v-col>
                            <v-col cols="12" sm="6" v-if="!formUtente.userId">
                                <v-text-field v-model="formUtente.password" type="password"
                                    label="Password provvisoria"
                                    :rules="[v => (!!v && v.length >= 8) || 'Almeno 8 caratteri']" />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="formUtente.displayName" label="Nome visualizzato" />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="formUtente.eMail" label="Email" />
                            </v-col>
                        </v-row>
                        <v-select v-model="formUtente.roleID" :items="ruoliDisponibili" item-title="label"
                            item-value="value" label="Ruolo" />
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn variant="text" @click="dialogUtente = false">Annulla</v-btn>
                    <v-btn color="primary" @click="salvaUtente">Salva</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog reset password -->
        <v-dialog v-model="dialogPassword" max-width="420">
            <v-card>
                <v-card-title>Reset password — {{ utenteSelezionato?.username }}</v-card-title>
                <v-card-text>
                    <v-text-field v-model="nuovaPassword" type="password" label="Nuova password provvisoria"
                        :rules="[v => (!!v && v.length >= 8) || 'Almeno 8 caratteri']" />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn variant="text" @click="dialogPassword = false">Annulla</v-btn>
                    <v-btn color="primary" @click="confermaResetPassword">Conferma</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog link di login automatico (embed) -->
        <v-dialog v-model="dialogEmbed" max-width="600">
            <v-card>
                <v-card-title>Link di login automatico — {{ utenteSelezionato?.username }}</v-card-title>
                <v-card-text>
                    <p class="text-body-2 text-medium-emphasis mb-4">
                        Apre il planner senza barra degli strumenti né menu laterale, già loggato come questo
                        utente. Da usare solo per integrare pagine del planner in una webview di un sistema
                        esterno affidabile: chi possiede il link accede come questo utente senza password.
                    </p>
                    <v-text-field v-model="embedRedirectPath" label="Pagina di destinazione" density="compact"
                        hint="Es. /calendario" persistent-hint class="mb-2" />

                    <template v-if="utenteSelezionato?.embedToken">
                        <v-text-field :model-value="embedUrl" label="Link" readonly density="compact">
                            <template #append-inner>
                                <v-icon size="small" style="cursor:pointer" @click="copiaEmbedUrl"
                                    title="Copia link">mdi-content-copy</v-icon>
                            </template>
                        </v-text-field>
                        <p class="text-caption text-medium-emphasis">
                            Per una pagina diversa non serve un nuovo link: cambia solo il campo "Pagina di
                            destinazione" sopra e copia di nuovo l'URL, il token resta lo stesso.
                        </p>
                    </template>
                    <template v-else>
                        <p class="text-body-2">Nessun link generato per questo utente.</p>
                    </template>
                </v-card-text>
                <v-card-actions>
                    <v-btn v-if="utenteSelezionato?.embedToken" variant="text" color="error"
                        @click="revocaLinkEmbed">Revoca</v-btn>
                    <v-spacer />
                    <v-btn variant="text" @click="dialogEmbed = false">Chiudi</v-btn>
                    <v-btn color="primary" @click="generaLinkEmbed">
                        {{ utenteSelezionato?.embedToken ? 'Rigenera' : 'Genera link' }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
export default {
    name: "UtentiPage",

    data() {
        return {
            headers: [
                { title: "Username", key: "username" },
                { title: "Nome", key: "displayName" },
                { title: "Email", key: "eMail" },
                { title: "Ruolo", key: "roleID" },
                { title: "Stato", key: "isDeleted" },
                { title: "", key: "azioni", sortable: false },
            ],
            ruoliDisponibili: [
                { label: "Operatore", value: "Operatore" },
                { label: "Visualizzatore", value: "Visualizzatore" },
            ],
            dialogUtente: false,
            formUtente: this.formUtenteVuoto(),
            dialogPassword: false,
            utenteSelezionato: null,
            nuovaPassword: "",
            dialogEmbed: false,
            embedRedirectPath: "/calendario",
        };
    },

    computed: {
        isMobile() {
            return this.$vuetify.display.smAndDown;
        },
        utenti() {
            return this.$store.getters["utenti/lista"];
        },
        embedUrl() {
            if (!this.utenteSelezionato?.embedToken) return "";
            const redirect = this.embedRedirectPath || "/";
            return `${window.location.origin}/embed-login?token=${this.utenteSelezionato.embedToken}&redirect=${encodeURIComponent(redirect)}`;
        },
    },

    mounted() {
        this.$store.dispatch("utenti/carica");
    },

    methods: {
        formUtenteVuoto() {
            return { userId: null, username: "", password: "", displayName: "", eMail: "", roleID: "Operatore" };
        },

        apriDialogUtente(item) {
            this.formUtente = item
                ? {
                    userId: item.userId,
                    username: item.username,
                    password: "",
                    displayName: item.displayName,
                    eMail: item.eMail,
                    roleID: item.roleID,
                }
                : this.formUtenteVuoto();
            this.dialogUtente = true;
        },

        async salvaUtente() {
            if (!this.$refs.formUtenteRef.validate()) return;
            try {
                const { userId, ...dati } = this.formUtente;
                await this.$store.dispatch("utenti/salva", { id: userId, dati });
                this.dialogUtente = false;
            } catch (error) {
                console.log(error);
            }
        },

        apriDialogPassword(item) {
            this.utenteSelezionato = item;
            this.nuovaPassword = "";
            this.dialogPassword = true;
        },

        async confermaResetPassword() {
            if (!this.nuovaPassword || this.nuovaPassword.length < 8) return;
            try {
                await this.$store.dispatch("utenti/resetPassword", {
                    id: this.utenteSelezionato.userId,
                    nuovaPassword: this.nuovaPassword,
                });
                this.dialogPassword = false;
            } catch (error) {
                console.log(error);
            }
        },

        async abilita(item) {
            try {
                await this.$store.dispatch("utenti/abilita", { id: item.userId });
            } catch (error) {
                console.log(error);
            }
        },

        async disabilita(item) {
            try {
                await this.$store.dispatch("utenti/disabilita", { id: item.userId });
            } catch (error) {
                console.log(error);
            }
        },

        apriDialogEmbed(item) {
            this.utenteSelezionato = item;
            this.dialogEmbed = true;
        },

        // Dopo generazione/revoca la lista viene ricaricata dallo store: risincronizzo
        // utenteSelezionato con l'item aggiornato per riflettere il nuovo token nel dialog.
        risincronizzaUtenteSelezionato() {
            const aggiornato = this.utenti.find(u => u.userId === this.utenteSelezionato?.userId);
            if (aggiornato) this.utenteSelezionato = aggiornato;
        },

        async generaLinkEmbed() {
            try {
                await this.$store.dispatch("utenti/generaEmbedToken", { id: this.utenteSelezionato.userId });
                this.risincronizzaUtenteSelezionato();
            } catch (error) {
                console.log(error);
            }
        },

        async revocaLinkEmbed() {
            try {
                await this.$store.dispatch("utenti/revocaEmbedToken", { id: this.utenteSelezionato.userId });
                this.risincronizzaUtenteSelezionato();
            } catch (error) {
                console.log(error);
            }
        },

        async copiaEmbedUrl() {
            try {
                await navigator.clipboard.writeText(this.embedUrl);
            } catch (error) {
                console.log(error);
            }
        },
    },
};
</script>
